import React, { useEffect, useState, useCallback } from "react";
import { api, fmt, fmtDate, todayISO, formatApiError } from "../lib/api";
import { Fuel, Plus, AlertTriangle, Trash2, RefreshCw } from "lucide-react";

function PageHeader() {
  return (
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
        <Fuel size={11} /> Fleet
      </div>
      <h1 className="font-black text-3xl tracking-tight">Diesel Log</h1>
      <p className="text-sm text-gray-500 mt-1">Per-vehicle diesel fill entries with odometer &amp; efficiency</p>
    </div>
  );
}

export function DieselPage() {
  const [vehicles, setVehicles]   = useState([]);
  const [holders, setHolders]     = useState([]);
  const [entries, setEntries]     = useState([]);
  const [alerts, setAlerts]       = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filterVeh, setFilterVeh] = useState("");
  const [lastOdo, setLastOdo]     = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: todayISO(), holder_id: "", vehicle_id: "", amount: "",
    litres: "", trip_km: "", driver_name: "", co_driver_name: "", bill_number: "", description: ""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, h, e, a] = await Promise.all([
        api.get("/vehicles?active=true"),
        api.get("/cash-holders"),
        api.get("/diesel/entries"),
        api.get("/diesel/alerts"),
      ]);
      setVehicles(v.data || []);
      setHolders(h.data || []);
      setEntries(e.data?.rows || []);
      setSummary(e.data?.summary || null);
      setAlerts(a.data?.alerts || []);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleVehicleChange = async (vid) => {
    setForm(f => ({...f, vehicle_id: vid}));
    if (!vid) { setLastOdo(null); return; }
    try {
      const r = await api.get(`/vehicles/${vid}/last-odometer`);
      setLastOdo(r.data);
    } catch { setLastOdo(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.holder_id) { setError("Select a cash holder"); return; }
    if (!form.vehicle_id) { setError("Select a vehicle"); return; }
    setSaving(true);
    setError("");
    try {
      await api.post("/expenses", {
        date: form.date, holder_id: form.holder_id, category: "Diesel",
        amount: parseFloat(form.amount || 0),
        litres: form.litres ? parseFloat(form.litres) : null,
        trip_km: form.trip_km ? parseFloat(form.trip_km) : null,
        vehicle_id: form.vehicle_id,
        driver_name: form.driver_name,
        co_driver_name: form.co_driver_name,
        bill_number: form.bill_number,
        description: form.description,
      });
      setForm(f => ({ ...f, amount: "", litres: "", trip_km: "", driver_name: "", co_driver_name: "", bill_number: "", description: "" }));
      setShowForm(false);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const deleteEntry = async (eid) => {
    if (!window.confirm("Delete this diesel entry?")) return;
    try { await api.delete(`/expenses/${eid}`); await load(); }
    catch (err) { setError(formatApiError(err)); }
  };

  const visibleEntries = filterVeh ? entries.filter(e => e.vehicle_id === filterVeh) : entries;

  return (
    <div className="px-6 py-6 max-w-5xl">
      <PageHeader />
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between">
          <span>{error}</span><button onClick={() => setError("")} className="font-bold ml-4">×</button>
        </div>
      )}

      {/* Alerts bar */}
      {alerts.length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-semibold text-amber-800">{alerts.length} Efficiency Alert{alerts.length > 1 ? "s" : ""}</div>
            <div className="text-xs text-amber-600 mt-0.5">
              {alerts.slice(0, 3).map((a, i) => (
                <span key={i} className="mr-2">
                  {a.vehicle_number}: {a.flag === "low" ? "Low" : "High"} avg ({a.actual_avg_kmpl} vs {a.expected_avg_kmpl} kmpl expected)
                </span>
              ))}
              {alerts.length > 3 && <span className="text-amber-500">+{alerts.length - 3} more</span>}
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Entries", val: summary.count },
            { label: "Total Amount", val: `₹${fmt(summary.total_amount)}` },
            { label: "Total Litres", val: `${summary.total_litres?.toFixed(1)} L` },
            { label: "With Alerts", val: summary.with_alert },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-black text-gray-900">{s.val}</div>
              <div className="text-xs text-gray-400 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-3 mb-4 items-center justify-between">
        <div className="flex gap-2 items-center">
          <select value={filterVeh} onChange={e => setFilterVeh(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="filter-vehicle">
            <option value="">All Vehicles</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.number} – {v.name}</option>)}
          </select>
          <button onClick={load} className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-700" data-testid="add-diesel-btn">
          <Plus size={14} /> Add Diesel Entry
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={submit} className="mb-6 bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <h3 className="font-bold text-sm text-gray-800">New Diesel Entry</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="diesel-date" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Vehicle *</label>
              <select value={form.vehicle_id} onChange={e => handleVehicleChange(e.target.value)} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="diesel-vehicle">
                <option value="">Select vehicle</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.number} – {v.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Cash Holder *</label>
              <select value={form.holder_id} onChange={e => setForm(f => ({...f, holder_id: e.target.value}))} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="diesel-holder">
                <option value="">Select holder</option>
                {holders.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Amount ₹ *</label>
              <input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} required placeholder="0.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="diesel-amount" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Litres</label>
              <input type="number" step="0.01" min="0" value={form.litres} onChange={e => setForm(f => ({...f, litres: e.target.value}))} placeholder="0.0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="diesel-litres" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Odometer (km)
                {lastOdo?.last_odometer_km && (
                  <span className="ml-2 text-gray-400 font-normal">prev: {lastOdo.last_odometer_km.toLocaleString("en-IN")} km</span>
                )}
              </label>
              <input type="number" min="0" value={form.trip_km} onChange={e => setForm(f => ({...f, trip_km: e.target.value}))} placeholder="Current odometer reading"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="diesel-odo" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Driver</label>
              <input value={form.driver_name} onChange={e => setForm(f => ({...f, driver_name: e.target.value}))} placeholder="Driver name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Bill No.</label>
              <input value={form.bill_number} onChange={e => setForm(f => ({...f, bill_number: e.target.value}))} placeholder="Petrol pump bill no."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Notes</label>
              <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Optional notes"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="bg-gray-900 text-white rounded-lg px-5 py-2 text-sm font-semibold hover:bg-gray-700 disabled:opacity-50" data-testid="save-diesel-btn">
              {saving ? "Saving…" : "Save Entry"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm text-gray-500 hover:text-gray-700 px-3">Cancel</button>
          </div>
        </form>
      )}

      {/* Entries table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="diesel-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {["Date","Vehicle","Holder","Odometer","KM Run","Litres","Amount ₹","Avg kmpl","Dev %","Driver","Notes",""].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleEntries.slice(0, 200).map((e, i) => {
                const absDev = e.deviation_pct ? Math.abs(e.deviation_pct) : 0;
                const rowFlag = absDev >= 15 ? (e.deviation_pct < 0 ? "low" : "high") : "ok";
                return (
                  <tr key={e.id} className={`border-b border-gray-50 ${rowFlag === "low" ? "bg-red-50/30" : rowFlag === "high" ? "bg-blue-50/20" : i % 2 === 0 ? "" : "bg-gray-50/40"}`}>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-800">{e.vehicle_number || "—"}<br /><span className="text-gray-400">{e.vehicle_name}</span></td>
                    <td className="px-4 py-2 text-gray-600">{holders.find(h => h.id === e.holder_id)?.name || "—"}</td>
                    <td className="px-4 py-2 font-mono text-gray-600">{e.trip_km != null ? e.trip_km.toLocaleString("en-IN") : "—"}</td>
                    <td className="px-4 py-2 font-mono text-gray-600">{e.km_run != null ? e.km_run.toLocaleString("en-IN") : "—"}</td>
                    <td className="px-4 py-2 font-mono text-gray-600">{e.litres != null ? Number(e.litres).toFixed(1) : "—"}</td>
                    <td className="px-4 py-2 font-mono text-right text-gray-800 font-semibold">₹{fmt(e.amount)}</td>
                    <td className="px-4 py-2 font-mono text-gray-600">{e.actual_avg_kmpl ? `${e.actual_avg_kmpl} kmpl` : "—"}</td>
                    <td className={`px-4 py-2 font-mono text-xs font-semibold ${rowFlag === "low" ? "text-red-600" : rowFlag === "high" ? "text-blue-600" : "text-gray-400"}`}>
                      {e.deviation_pct != null ? `${e.deviation_pct > 0 ? "+" : ""}${e.deviation_pct}%` : "—"}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{e.driver_name}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs max-w-[100px] truncate">{e.description}</td>
                    <td className="px-4 py-2">
                      <button onClick={() => deleteEntry(e.id)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={12} /></button>
                    </td>
                  </tr>
                );
              })}
              {visibleEntries.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-8 text-center text-sm text-gray-400">
                  {loading ? "Loading…" : "No diesel entries yet"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DieselPage;
